import { BookOpen, Bot, Lightbulb, Search } from 'lucide-react'
import OpenClawLandingPage from '../components/openclaw/OpenClawLandingPage'
import Baidu2DownloadButtons from '../components/openclaw/Baidu2DownloadButtons'
import useBaidu2Tracking from '../hooks/useBaidu2Tracking'

function OpenClawWhatIs() {
  const { bdVid, landingUrl } = useBaidu2Tracking('what-is')

  return (
    <OpenClawLandingPage
      downloadContent={<Baidu2DownloadButtons theme="violet" bdVid={bdVid} landingUrl={landingUrl} landingPage="what-is" />}
      theme="violet"
      source="openclaw-what-is"
      seo={{
        title: '龙虾AI快速了解：龙虾AI是什么？',
        description: '快速解释龙虾AI是什么、它和 OpenClaw 的关系，以及“Claw”这名字的含义，并提供官网入口、下载入口与上手指引。',
        keywords: '龙虾AI是什么, 龙虾AI, OpenClaw是什么, CLAW是什么意思, OpenClaw官网'
      }}
      hero={{
        eyebrow: '龙虾AI 是什么',
        title: '龙虾AI 是什么？',
        subtitle: '它和 OpenClaw 什么关系，“Claw”又是什么意思',
        description:
          '很多人搜索“龙虾AI是什么”，本质上是在找一个更好理解的入口：它到底是不是一个能用的软件？能做什么？要去哪里下载？\n这个页面会先把“龙虾AI”和 OpenClaw 的关系讲清楚，再带你顺着官网入口与下载区完成体验。',
        leadBullets: ['解释龙虾AI与 OpenClaw 的对应关系', '说明“Claw”名字的含义与品牌表达', '提供官网与下载入口，理解后可直接体验'],
        tags: ['龙虾AI是什么', 'OpenClaw关系', '名字解释', '下载入口'],
        guideTitle: '先把“龙虾AI”理解清楚，再决定要不要下载',
        guideDescription: '相比一上来就让人下载，一个好的落地页应该先回答“我在找的东西到底是什么”。这里用更贴近中文搜索习惯的方式，把认知问题先解决掉。',
        guideBullets: [
          '先回答“龙虾AI是什么”：它对应的是 OpenClaw 这类桌面 AI 助手体验。',
          '再把“OpenClaw/Claw”这些名字对上号，避免看完仍不确定是不是同一个产品。',
          '最后再进入下载区，用实际界面验证你的理解。'
        ]
      }}
      screenshotCaption="理解“龙虾AI”和 OpenClaw 的关系后，再看客户端截图会更容易把中文认知与实际体验对应起来。"
      highlights={[
        {
          icon: Search,
          title: '先解释龙虾AI是什么',
          description: '先把“龙虾AI是什么、对应什么产品、是否能直接下载体验”讲清楚，再进入功能与上手。'
        },
        {
          icon: Bot,
          title: '把龙虾AI和 OpenClaw 对上号',
          description: '避免出现“我以为在找龙虾AI，结果跳到 OpenClaw 不确定是不是同一个”的疑惑。'
        },
        {
          icon: BookOpen,
          title: '看懂之后再进入下载',
          description: '先理解定位，再进入下载区体验，转化路径更顺、体验也更稳定。'
        }
      ]}
      steps={[
        {
          title: '龙虾AI是什么？',
          description: '在这里，“龙虾AI”是面向中文用户的直观叫法，它对应的是 OpenClaw 这类桌面 AI 助手产品体验。'
        },
        {
          title: 'OpenClaw 和 Claw 是什么意思？',
          description: 'OpenClaw 是产品名称；Claw 是其中的核心品牌识别词。对普通用户来说，你只需要记住：龙虾AI通常就是在说 OpenClaw。'
        },
        {
          title: '为什么大家会用“龙虾AI”来称呼？',
          description: '因为中文语境下更容易记住和传播，也更容易在搜索时命中“我想找的那个桌面 AI 助手”。'
        }
      ]}
      sections={[
        {
          title: '搜索“龙虾AI是什么”的用户通常在找什么？',
          description: '他们往往不是马上就想安装，而是想先确认：这是不是一个能用的软件、对应哪个产品、值不值得继续了解。',
          items: [
            '这是不是一个真正可以使用的软件，而不是纯概念项目。',
            '它和一般的 AI 对话工具有什么不同。',
            '官网在哪里，是否有中文介绍可看。',
            '如果我看懂了，后面能不能直接下载安装。'
          ]
        },
        {
          title: '为什么“先理解再下载”很重要？',
          description: '因为名字、品牌、功能定位不清楚时，用户很容易下载完却不知道它适合干什么，体验就会打折扣。',
          items: [
            '先理解龙虾AI对应什么产品，可以减少试错。',
            '先把名称关系弄清楚，更容易记住品牌。',
            '先知道适合谁，再决定是否安装，会更理性。',
            '先建立认知，再看下载入口，转化质量更高。'
          ]
        }
      ]}
      faq={[
        {
          q: '龙虾AI和 OpenClaw 是同一个东西吗？',
          a: '在这个页面里，你可以把“龙虾AI”理解为更贴近中文搜索习惯的叫法，它对应的是 OpenClaw 这款桌面 AI 助手产品与官网入口。'
        },
        {
          q: '我只记得“龙虾AI”，怎么找到官方下载？',
          a: '你可以直接在本页往下滑到下载区下载安装；页面也会通过导航与说明把官网入口和下载入口放在同一个路径里。'
        },
        {
          q: '为什么会出现“Claw / OpenClaw”这些名字？',
          a: '它们是产品与品牌命名方式。对普通用户来说，不需要从技术角度理解，只要知道它们对应同一套产品体验与下载入口即可。'
        },
        {
          q: '看完之后可以直接下载体验吗？',
          a: '可以。页面底部保留了下载区，支持选择 Windows / Mac Intel / Mac Apple 版本直接下载安装体验。'
        }
      ]}
      closing={{
        title: '已经弄清楚龙虾AI是什么了？',
        description: '如果你已经了解了“龙虾AI”和 OpenClaw 的关系以及产品定位，可以直接继续下载体验，用实际界面和使用过程验证你的理解。',
        footerTitle: '先把中文认知对齐，再开始体验',
        footerDescription: '这个页面优先解决“龙虾AI是什么、对应哪个产品、怎么进入下载”的问题，然后再顺势进入官网与安装流程。',
        footerTags: ['龙虾AI是什么', 'OpenClaw关系', 'Claw是什么意思', '下载入口']
      }}
    />
  )
}

export default OpenClawWhatIs
